import { api } from '@/api';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import { readStayEntitlements } from '@/api/core/entitlements';
import { readAdminStay } from '@/api/core/stays';
import type { ServiceClient } from '@/api/http/client';
import { isApiError } from '@/api/http/errors';
import { operatorConfig, operatorSession, useOperatorSession } from '@/auth/runtime';
import type { OperatorSession } from '@/auth/session';
import { displayStayStatus, StayPeriod } from '@/pages/stays/stay-presentation';
import { Button } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

export interface AccessRuntime {
  client: ServiceClient;
  session: OperatorSession;
  apiNamespace: string;
}
const accessRuntime: AccessRuntime = {
  client: api.core,
  session: operatorSession,
  apiNamespace: operatorConfig?.coreApiUrl ?? 'unconfigured',
};
const activeStayBySession = new WeakMap<OperatorSession, string>();
const stayKey = (runtime: AccessRuntime, generation: number, stayId: string) =>
  coreQueryKey(runtime.apiNamespace, generation, 'admin-stay', stayId);
const entitlementKey = (runtime: AccessRuntime, generation: number, stayId: string) =>
  coreQueryKey(runtime.apiNamespace, generation, 'stay-entitlements', stayId);

function accessRead<T>(
  runtime: AccessRuntime,
  domain: string,
  generation: number,
  stayId: string,
  signal: AbortSignal,
  isCurrentStay: () => boolean,
  read: () => Promise<T>,
): Promise<T> {
  return read().catch((error: unknown) => {
    if (
      isApiError(error) &&
      error.status === 403 &&
      !signal.aborted &&
      runtime.session.getSnapshot().generation === generation &&
      isCurrentStay() &&
      activeStayBySession.get(runtime.session) === stayId
    )
      runtime.session.reportDomainAccessDenied(domain, generation);
    throw error;
  });
}

function Failure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const isBudgetError = isApiError(error) && error.status === 503;
  return (
    <div role="alert" className="space-y-2">
      <p>
        {isBudgetError
          ? '저장 관측을 확인할 수 없습니다. 일부 결과만 표시하지 않습니다.'
          : '저장 관측 조회에 실패했습니다.'}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        다시 조회
      </Button>
    </div>
  );
}

function StayPanel({
  stayId,
  runtime,
  generation,
  enabled,
  showStaySummary,
}: {
  stayId: string;
  runtime: AccessRuntime;
  generation: number;
  enabled: boolean;
  showStaySummary: boolean;
}) {
  const stay = useQuery({
    queryKey: stayKey(runtime, generation, stayId),
    queryFn: ({ signal }) =>
      accessRead(
        runtime,
        'booking',
        generation,
        stayId,
        signal,
        () => activeStayBySession.get(runtime.session) === stayId,
        () => readAdminStay(runtime.client, stayId, signal),
      ),
    enabled,
    retry: false,
    meta: { suppressGlobalError: true },
  });
  if (!showStaySummary) return null;
  if (!enabled) return <p role="status">현재 운영 권한으로 투숙 저장 상태를 조회할 수 없습니다.</p>;
  if (stay.isPending) return <p role="status">투숙 저장 상태를 조회하고 있습니다.</p>;
  if (stay.isError)
    return (
      <Failure
        error={stay.error}
        onRetry={() => {
          void stay.refetch();
        }}
      />
    );
  return (
    <dl className="grid gap-2 sm:grid-cols-2">
      <div>
        <dt>현재 저장 투숙 상태</dt>
        <dd>{displayStayStatus(stay.data.status)}</dd>
      </div>
      <div>
        <dt>투숙 저장 기간</dt>
        <dd>
          <StayPeriod startAt={stay.data.start_at} endAt={stay.data.end_at} />
        </dd>
      </div>
      <div className="sm:col-span-2">
        <dt>연결 예약의 저장 정보</dt>
        <dd>
          {stay.data.sources.length ? (
            <ul>
              {stay.data.sources.map((source) => (
                <li key={source.source_id} className="space-y-1">
                  <span>{source.status}</span>
                  <StayPeriod startAt={source.start_at} endAt={source.end_at} />
                </li>
              ))}
            </ul>
          ) : (
            '연결 예약의 저장 정보가 없습니다.'
          )}
        </dd>
      </div>
    </dl>
  );
}

function EntitlementPanel({
  stayId,
  runtime,
  generation,
  enabled,
}: {
  stayId: string;
  runtime: AccessRuntime;
  generation: number;
  enabled: boolean;
}) {
  const entitlements = useQuery({
    queryKey: entitlementKey(runtime, generation, stayId),
    queryFn: ({ signal }) =>
      accessRead(
        runtime,
        'entitlement',
        generation,
        stayId,
        signal,
        () => activeStayBySession.get(runtime.session) === stayId,
        () => readStayEntitlements(runtime.client, stayId, signal),
      ),
    enabled,
    retry: false,
    meta: { suppressGlobalError: true },
  });
  if (!enabled)
    return (
      <p role="status">출입 자격 조회 권한이 없습니다. 투숙 상태 조회와는 별도로 제한됩니다.</p>
    );
  if (entitlements.isPending)
    return <p role="status">저장된 출입 issue와 grant를 조회하고 있습니다.</p>;
  if (entitlements.isError)
    return (
      <Failure
        error={entitlements.error}
        onRetry={() => {
          void entitlements.refetch();
        }}
      />
    );
  const { issues, grants } = entitlements.data;
  return (
    <div className="space-y-4">
      <section aria-labelledby="access-issues-title">
        <h4 id="access-issues-title" className="font-medium">
          출입 권한 발급 요청
        </h4>
        {issues.length ? (
          <ul className="space-y-2">
            {issues.map((issue) => (
              <li key={issue.command_id}>
                <span>{issue.status}</span>
                <span>
                  {' '}
                  · 발급 요청 생성 당시 투숙 {issue.stay_status}, 사용자 {issue.user_status}
                </span>
                <span> · 저장 resource {issue.resource_id ?? '확인할 수 없음'}</span>
                {issue.status === 'UNKNOWN' && <span> · 결과 미확정</span>}
                <span> · 저장 credential generation {issue.credential_generation}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p>저장된 출입 권한 발급 요청이 없습니다.</p>
        )}
      </section>
      <section aria-labelledby="access-grants-title">
        <h4 id="access-grants-title" className="font-medium">
          저장된 출입 권한 및 회수 기록
        </h4>
        {grants.length ? (
          <ul className="space-y-3">
            {grants.map((grant) => (
              <li key={grant.entitlement_id}>
                <p>
                  {grant.status} · 저장 credential generation {grant.credential_generation}
                </p>
                <p>저장 resource {grant.resource_id ?? '확인할 수 없음'}</p>
                <p>
                  ownership epoch {grant.ownership_epoch} · claim generation{' '}
                  {grant.claim_generation} · aggregate revision {grant.aggregate_revision}
                </p>
                {grant.revocation ? (
                  <p>
                    저장 회수 상태 {grant.revocation.status} · 회수 generation{' '}
                    {grant.revocation.credential_generation} · 원 grant generation{' '}
                    {grant.revocation.original_credential_generation}
                    {grant.revocation.operator_action_required && ' · 현장 운영자 조치 필요'}
                    {grant.revocation.status === 'VERIFIED_REVOKED' && ' · 저장된 회수 결과'}
                    {' · 자동 물리 회수 허용: '}
                    {grant.revocation.automatic_physical_revocation_allowed ? '예' : '아니요'}
                  </p>
                ) : (
                  <p>저장된 회수 관측이 없습니다.</p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>저장된 출입 권한이 없습니다.</p>
        )}
      </section>
      <section aria-labelledby="access-mapping-title">
        <h4 id="access-mapping-title" className="font-medium">
          Core LoRa 공간 매핑 관측
        </h4>
        {entitlements.data.access_mapping.status === 'UNAVAILABLE' ? (
          <p>LoRa 매핑 관측을 확인할 수 없습니다.</p>
        ) : entitlements.data.access_mapping.groups.length ? (
          <ul>
            {entitlements.data.access_mapping.groups.map((group) => (
              <li key={group.group_id}>
                저장 group {group.group_id} · 공유 slot {group.shared_slot_count}개
              </li>
            ))}
          </ul>
        ) : (
          <p>현재 관측된 LoRa 매핑 그룹이 없습니다. 전체 무장비를 뜻하지 않습니다.</p>
        )}
        <p>온라인 Clue 권한은 포함되지 않습니다.</p>
      </section>
      <p className="text-muted-foreground">
        저장된 기록이며 실제 장비의 현재 상태를 나타내지 않습니다.
      </p>
    </div>
  );
}

export function StayAccessSection({
  stayId,
  runtime = accessRuntime,
  showStaySummary = true,
}: {
  stayId: string | null;
  runtime?: AccessRuntime;
  showStaySummary?: boolean;
}) {
  const session = useOperatorSession(runtime.session);
  useEffect(() => {
    if (stayId) activeStayBySession.set(runtime.session, stayId);
    else activeStayBySession.delete(runtime.session);
    return () => {
      if (activeStayBySession.get(runtime.session) === stayId)
        activeStayBySession.delete(runtime.session);
    };
  }, [runtime.session, stayId]);
  if (session.status !== 'authorized') return null;
  if (!stayId)
    return (
      <section aria-label="투숙 출입 저장 관측">
        <p>이 예약은 저장 투숙에 연결되어 있지 않습니다.</p>
      </section>
    );
  const authorization = session.authorization;
  if (!authorization) return null;
  const bookingAllowed =
    canReadDomain(authorization, 'booking', 2) && !session.deniedDomains.includes('booking');
  const entitlementAllowed =
    canReadDomain(authorization, 'entitlement', 1) &&
    !session.deniedDomains.includes('entitlement');
  return (
    <section aria-label="투숙 출입 저장 관측" className="space-y-5">
      {showStaySummary && (
        <div>
          <h3 className="title-sm">투숙 현재 저장 상태</h3>
          <StayPanel
            stayId={stayId}
            runtime={runtime}
            generation={session.generation}
            enabled={bookingAllowed}
            showStaySummary={showStaySummary}
          />
        </div>
      )}
      <div>
        <h3 className="title-sm">저장된 출입 관측</h3>
        <EntitlementPanel
          stayId={stayId}
          runtime={runtime}
          generation={session.generation}
          enabled={entitlementAllowed}
        />
      </div>
    </section>
  );
}
